<template>
  <PlantillaLogin>
    <div class="login-layout">
      <section class="login-intro" aria-label="Bienvenida">
        <div class="login-brand">
          <img class="login-brand-logo" src="/images/Logo-SSM.jpg" alt="Logo del Servicio de Salud Magallanes" />
          <span>Servicio de Salud Magallanes</span>
        </div>
        <div class="login-intro-copy">
          <span class="login-eyebrow">Plataforma de encuestas</span>
          <h1>Tu espacio para participar</h1>
          <p>Ingresa con tus credenciales para revisar y responder tus encuestas.</p>
        </div>
        <p class="login-intro-footer">Servicio de Salud Magallanes</p>
      </section>

      <section class="login-panel" aria-labelledby="login-title">
        <div class="login-card">
          <div class="login-mobile-brand">
            <img class="login-brand-logo" src="/images/Logo-SSM.jpg" alt="Logo del Servicio de Salud Magallanes" />
            <span>Servicio de Salud Magallanes</span>
          </div>
          <span class="login-eyebrow">Bienvenido</span>
          <h2 id="login-title">Iniciar sesión</h2>
          <p class="login-description">Ingresa tu RUT y contraseña para continuar.</p>

          <form @submit.prevent="conectar">
            <div class="field">
              <label for="rut" class="label">RUT</label>
              <div class="control">
                <input
                  id="rut"
                  class="input login-input"
                  type="text"
                  :value="formattedRut"
                  @input="onRutInput"
                  placeholder="12.345.678-9"
                  autocomplete="username"
                  autocapitalize="characters"
                  spellcheck="false"
                  maxlength="12"
                  aria-describedby="rut-help"
                  autofocus
                  required
                />
              </div>
              <p id="rut-help" class="help">Los puntos y el guion se agregan automáticamente.</p>
            </div>

            <div class="field">
              <label for="password" class="label">Contraseña</label>
              <div class="field has-addons password-field">
                <div class="control is-expanded">
                  <input
                    id="password"
                    v-model="user.password"
                    class="input login-input"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Tu contraseña"
                    autocomplete="current-password"
                    required
                  />
                </div>
                <div class="control">
                  <button
                    class="button password-toggle"
                    type="button"
                    :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                    :aria-pressed="showPassword.toString()"
                    @click="showPassword = !showPassword"
                  >{{ showPassword ? 'Ocultar' : 'Mostrar' }}</button>
                </div>
              </div>
            </div>

            <b-message v-if="error" type="is-danger" role="alert" class="login-error">{{ error }}</b-message>

            <button class="button login-submit is-fullwidth" type="submit" :disabled="isLoading">
              <span>{{ isLoading ? 'Ingresando...' : 'Ingresar' }}</span>
              <span v-if="!isLoading" aria-hidden="true">→</span>
            </button>
          </form>
        </div>
      </section>
    </div>
  </PlantillaLogin>
</template>

<script>
import PlantillaLogin from "../template/PlantillaLogin";
import { formatRut, normalizeRut } from "../utils/rut";

export default {
  name: "Login",
  components: { PlantillaLogin },
  data() {
    return {
      user: { rut: "", password: "", grant_type: "password", token: "", role_id: "" },
      error: "",
      isLoading: false,
      showPassword: false,
    };
  },
  computed: {
    formattedRut() {
      return formatRut(this.user.rut);
    },
  },
  methods: {
    onRutInput(event) {
      const input = event.target;
      const charactersBeforeCaret = normalizeRut(input.value.slice(0, input.selectionStart)).length;
      this.user.rut = normalizeRut(input.value);
      this.error = "";

      this.$nextTick(() => {
        input.value = this.formattedRut;
        let caret = 0;
        let characters = 0;
        while (caret < input.value.length && characters < charactersBeforeCaret) {
          if (/[0-9K]/.test(input.value.charAt(caret))) characters++;
          caret++;
        }
        input.setSelectionRange(caret, caret);
      });
    },
    conectar() {
      if (this.isLoading) return;
      this.error = "";
      this.isLoading = true;
      this.$http
        .post("api/auth", {
          rut: normalizeRut(this.user.rut),
          password: this.user.password,
          grant_type: this.user.grant_type,
        })
        .then((response) => {
          this.user.role_id = Number(response.body.user.role_id);
          if (this.user.role_id !== 2 && this.user.role_id !== 3) {
            this.error = "Tu perfil no tiene acceso a esta plataforma.";
            this.isLoading = false;
            return;
          }
          this.user.token = response.body.token;
          window.localStorage.setItem("token", response.body.token);
          window.localStorage.setItem(
            "userName",
            `${response.body.user.name} ${response.body.user.pather_lastname} ${response.body.user.mother_lastname}`
          );
          window.localStorage.setItem("perfil", response.body.user.role.type);
          window.localStorage.setItem("user", JSON.stringify(response.body.user));
          if (this.user.role_id === 2) this.$router.push({ name: "administrativeHome" });
          else if (this.user.role_id === 3) this.$router.push({ name: "questionary" });
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.error = "RUT o contraseña incorrectos. Revisa tus datos e inténtalo nuevamente.";
        });
    },
  },
};
</script>

<style scoped>
.login-layout { display: grid; grid-template-columns: minmax(300px, 43%) 1fr; min-height: 100vh; }
.login-intro { display: flex; flex-direction: column; justify-content: space-between; padding: clamp(2rem, 5vw, 5rem); color: white; background: linear-gradient(155deg, #12465b 0%, #087478 58%, #1b9170 100%); }
.login-brand { display: flex; align-items: center; gap: .85rem; font-size: 1rem; font-weight: 700; letter-spacing: .01em; }
.login-brand-logo { flex: none; width: 5rem; height: 4.5rem; object-fit: contain; border-radius: 10px; background: white; }
.login-intro-copy { max-width: 31rem; }
.login-eyebrow { color: #1b7877; font-size: .78rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; }
.login-intro .login-eyebrow { color: #b8f1d6; }
.login-intro h1 { margin: 1rem 0; color: white; font-size: clamp(2.3rem, 4vw, 4rem); font-weight: 700; line-height: 1.12; }
.login-intro-copy p { color: #e2f5f1; font-size: 1.13rem; line-height: 1.65; }
.login-intro-footer { color: #d0eeea; font-size: .88rem; }
.login-panel { display: grid; place-items: center; padding: 3rem 1.5rem; background: #f6faf9; }
.login-card { width: min(100%, 430px); padding: clamp(1.75rem, 4vw, 3rem); border: 1px solid #e3ecea; border-radius: 20px; background: white; box-shadow: 0 20px 55px rgba(17, 69, 77, .09); }
.login-mobile-brand { display: none; align-items: center; gap: .8rem; margin-bottom: 2rem; color: #164f58; font-weight: 700; }
.login-card h2 { margin: .5rem 0 .4rem; color: #173b47; font-size: 2rem; font-weight: 700; }
.login-description { margin-bottom: 2rem; color: #62747a; line-height: 1.5; }
.login-card .field { margin-bottom: 1.35rem; }
.login-card .label { color: #29474f; font-size: .9rem; }
.login-input { height: 3rem; border-color: #cbdcda; border-radius: 9px; box-shadow: none; }
.login-input:focus { border-color: #14827b; box-shadow: 0 0 0 3px rgba(20, 130, 123, .13); }
.login-card .help { color: #687c80; font-size: .79rem; }
.password-toggle { height: 3rem; border-color: #cbdcda; border-radius: 0 9px 9px 0; color: #1a706e; font-weight: 600; }
.password-field .login-input { border-radius: 9px 0 0 9px; }
.login-submit { display: flex; justify-content: space-between; height: 3.15rem; margin-top: 1.75rem; padding: 0 1.2rem; border: 0; border-radius: 9px; background: #087b72; color: white; font-weight: 700; }
.login-submit:hover, .login-submit:focus { background: #06655e; color: white; }
.login-submit:disabled { opacity: .7; cursor: wait; }
.login-error { margin-bottom: 1rem; }
@media (max-width: 768px) {
  .login-layout { grid-template-columns: 1fr; }
  .login-intro { display: none; }
  .login-panel { min-height: 100vh; padding: 1.25rem; }
  .login-card { padding: 2rem 1.5rem; }
  .login-mobile-brand { display: flex; }
  .login-mobile-brand .login-brand-logo { width: 4rem; height: 3.6rem; }
}
</style>
